import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScanLine, GraduationCap } from "lucide-react";

export default function Login() {
  const { login, user, booted } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (booted && user) nav("/"); }, [booted, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (ex) {
      setErr(apiError(ex, "Giriş başarısız"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="relative z-10 flex items-center gap-3">
          <img src="/logo192.png" alt="Logo" className="h-11 w-11 rounded-xl object-cover bg-white/15" />
          <span className="text-lg font-bold tracking-tight">HAFTALIK KTT</span>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight">Konu Tarama Testi<br />Sınav & Optik Sistemi</h1>
          <p className="text-primary-foreground/80 max-w-md leading-relaxed">
            Sınav oluşturma, cevap anahtarı, optik form, QR kod, otomatik puanlama, sınıf ve öğrenci analizi — hepsi tek platformda.
          </p>
          <div className="flex gap-6 pt-4">
            <div className="flex items-center gap-2"><ScanLine className="h-5 w-5" /><span className="text-sm">Optik Okuma</span></div>
            <div className="flex items-center gap-2"><GraduationCap className="h-5 w-5" /><span className="text-sm">Net & Puan</span></div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-primary-foreground/60">© 2026 Haftalık KTT</div>
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full bg-white/5" />
        <div className="absolute right-20 top-20 h-48 w-48 rounded-full bg-white/5" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-2">
            <img src="/logo192.png" alt="Logo" className="h-10 w-10 rounded-lg object-cover" />
            <span className="font-bold">HAFTALIK KTT</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Giriş Yap</h2>
            <p className="text-sm text-muted-foreground mt-1">Yönetim paneline erişmek için giriş yapın.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-posta</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Şifre</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" placeholder="••••••••" />
          </div>
          {err && <div className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2" data-testid="login-error">{err}</div>}
          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
            {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
          </Button>
          <p className="text-sm text-center text-muted-foreground">Kurumunuz kayıtlı değil mi? <Link to="/kayit" className="text-primary font-semibold" data-testid="login-register-link">7 gün ücretsiz deneyin</Link></p>
        </form>
      </div>
    </div>
  );
}
